import { z } from 'zod';
import { AuditSchema, DateOnlySchema, IdSchema, InstantSchema } from './common.ts';
import { MoneySchema, type Money } from '../money.ts';

/**
 * Bid packages and the bids against them (SCRUM-143, P8-10).
 *
 * `docs/features/13-later-modules.md`: "Bid packages per job, invite subs,
 * sub submits bid in portal, compare, accept → creates PO with scope;
 * 'Related Bids' on PO."
 *
 * **The whole module turns on one thing: a trade must never see another
 * trade's price.** Everything else here is ordinary record-keeping; that
 * is the part that would end a builder's relationship with their subs if
 * it leaked, and it is why a bid is a document of its own under the
 * package rather than a field on it — Firestore rules are all-or-nothing
 * per document, so prices kept on the package would be in the bytes every
 * invited sub received, whatever the screen drew.
 */
export const BID_PACKAGE_STATUSES = ['draft', 'out', 'closed', 'awarded'] as const;
export const BidPackageStatusSchema = z.enum(BID_PACKAGE_STATUSES);
export type BidPackageStatus = z.infer<typeof BidPackageStatusSchema>;

export const BID_PACKAGE_STATUS_LABEL: Record<BidPackageStatus, string> = {
  draft: 'Not sent',
  out: 'Out to bid',
  closed: 'Closed',
  awarded: 'Awarded',
};

export const BidPackageSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  title: z.string().trim().min(1, 'A package needs a title').max(200),
  /** What is being priced, in the words that go to the trades. */
  scope: z.string().trim().max(20_000).default(''),
  costCodeId: IdSchema.optional(),
  status: BidPackageStatusSchema,
  /** When bids are wanted by. Optional, like an RFI's. */
  dueDate: DateOnlySchema.optional(),
  /**
   * The trades it was sent to.
   *
   * On the package rather than inferred from the bids, because an
   * invitation that produced no bid is the thing a builder most wants to
   * see: four asked, one priced.
   */
  invitedContactIds: z.array(IdSchema).default([]),
  /** Set when a bid is accepted. The package is `awarded` from then on. */
  awardedBidId: IdSchema.optional(),
  /**
   * The purchase order raised from the award, once there is one.
   *
   * **Separate from `awardedBidId`, and deliberately later.** Awarding is
   * a decision the builder makes; raising the order is a numbered
   * instrument that costs a server transaction against a counter. Putting
   * them in one step would make the decision depend on a callable
   * succeeding, and leave a package neither awarded nor not when it
   * failed. So the award stands on its own and this field is written
   * afterwards — empty here means "awarded, no order raised yet", which
   * is a real state a builder sits in for a day.
   */
  purchaseOrderId: IdSchema.optional(),
  version: z.number().int().positive(),
});
export type BidPackage = z.infer<typeof BidPackageSchema>;

export const BidSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  packageId: IdSchema,
  /** Whose bid it is. A directory contact, who may have no login. */
  contactId: IdSchema,
  amount: MoneySchema,
  notes: z.string().trim().max(20_000).default(''),
  submittedAt: InstantSchema,
  /**
   * Withdrawn rather than deleted.
   *
   * A trade who pulls out has still been asked and still answered, and a
   * builder comparing four bids needs to know the fourth existed. Deleting
   * it would make the package read as though three were invited.
   */
  withdrawn: z.boolean().default(false),
  version: z.number().int().positive(),
});
export type Bid = z.infer<typeof BidSchema>;

/**
 * Where a package may go next.
 *
 * `out` can return to `draft`, which is the edge worth stating: a package
 * sent with the wrong scope is pulled back, corrected and sent again, and
 * the alternative is a second package competing with the first. `awarded`
 * is terminal — the work is let.
 */
export const BID_PACKAGE_TRANSITIONS: Record<BidPackageStatus, readonly BidPackageStatus[]> = {
  draft: ['out'],
  out: ['draft', 'closed'],
  closed: ['out', 'awarded'],
  awarded: [],
};

export function canTransitionPackage(from: BidPackageStatus, to: BidPackageStatus): boolean {
  return BID_PACKAGE_TRANSITIONS[from].includes(to);
}

/**
 * Whether a trade may see this package at all.
 *
 * A **draft** is invisible to everybody outside, which is the point of
 * having one: a builder writes the scope over a morning and does not want
 * four trades reading half of it. Once it is out it stays visible to the
 * people invited, including after it closes — a trade is entitled to know
 * the job they priced was let.
 */
export function isPackageVisibleToTrades(pkg: Pick<BidPackage, 'status'>): boolean {
  return pkg.status !== 'draft';
}

export function isPackageOpenForBids(pkg: Pick<BidPackage, 'status'>): boolean {
  return pkg.status === 'out';
}

/** The bids that still count: submitted and not withdrawn. */
export function liveBids(bids: readonly Bid[]): Bid[] {
  return bids.filter((bid) => !bid.withdrawn);
}

/**
 * The cheapest live bid, or null when nobody has priced it.
 *
 * **Named `lowest`, not `best`.** The cheapest bid is not always the one a
 * builder takes, and a function called `best` would quietly make that
 * decision for them — the comparison screen shows every bid and this only
 * marks one of them.
 */
export function lowestBid(bids: readonly Bid[]): Bid | null {
  const live = liveBids(bids);
  if (live.length === 0) return null;
  return live.reduce((cheapest, bid) =>
    bid.amount.amount < cheapest.amount.amount ? bid : cheapest,
  );
}

/**
 * How far apart the bids are, as a spread between lowest and highest.
 *
 * A wide spread on a well-written scope usually means the scope was not
 * well written — the trades are pricing different work. Worth surfacing
 * rather than leaving somebody to notice.
 */
export function bidSpread(bids: readonly Bid[]): { low: Money; high: Money } | null {
  const live = liveBids(bids);
  if (live.length === 0) return null;
  const amounts = live.map((bid) => bid.amount);
  const low = amounts.reduce((least, one) => (one.amount < least.amount ? one : least));
  const high = amounts.reduce((most, one) => (one.amount > most.amount ? one : most));
  return { low, high };
}

/**
 * Who was asked and has not answered.
 *
 * The question a builder asks two days before bids are due, and the
 * reason `invitedContactIds` lives on the package: without it, "nobody
 * else was asked" and "two others were asked and said nothing" look the
 * same.
 */
export function awaitingBids(
  pkg: Pick<BidPackage, 'invitedContactIds'>,
  bids: readonly Bid[],
): string[] {
  const answered = new Set(liveBids(bids).map((bid) => bid.contactId));
  return pkg.invitedContactIds.filter((contactId) => !answered.has(contactId));
}
